import { take, put, takeEvery, call, select } from "redux-saga/effects";
import { push as navigateTo } from 'connected-react-router';
import { Actions, receiveEcsCallIdMessage, getVideoCallEventsRequest, getVideoCallEventsFailure, getVideoCallEventsSuccess } from "actions/ecsActions";
import { apiGet } from "./apiSagas";
import { getVideoCallEvents } from "selectors/ecsSelectors";
import { VideoCallEvent, CallId } from 'shared/models';
import { toError } from 'shared/util/toError';

export function *ecsCallIdWatcher() {
    while (true) {
        // Handle the callback case and unsolicited call case where the user
        // issues an MSCL websocket message to initiate a call but we don't
        // have a call ID yet
        const action: ReturnType<typeof receiveEcsCallIdMessage> = yield take(Actions.ECS_CLID_MSG);
        const { callId } = action.payload;
        if (callId) {
            yield put(navigateTo(`/video/${callId}`));
        }
    }
}

export function *ecsEventsRequestWatcher() {
    yield takeEvery(Actions.GET_VC_EVENTS_REQUEST, ecsEventsRequestSaga);
}

function *ecsEventsRequestSaga(action: ReturnType<typeof getVideoCallEventsRequest>) {
    const callId = action.payload;
    const { frames = false } = action.meta || {};
    yield call(getCallEvents, callId, { frames });
}

function *getCallEvents(callId: CallId, { frames }: { frames: boolean }) {
    let lastId = 0;
    let i = 0;
    while (i++ < 1000) {
        const existingEvents: VideoCallEvent[] | null = yield select(getVideoCallEvents, { callId, frames });
        if (existingEvents) {
            if (lastId === existingEvents.slice(-1)[0].id) {
                // this should never happen... just a failsafe to prevent unnecessary looping in case a bizarre error happens
                return true;
            }
            lastId = existingEvents.slice(-1)[0].id;
        }

        const queryStringParts = [
            existingEvents
                ? `since=${lastId}`
                : '',
            frames
                ? 'frames=1'
                : '',
        ].filter(p => !!p);

        const queryString =
            queryStringParts.length
                ? `?${queryStringParts.join('&')}`
                : '';

        try {
            const { events, more } = yield call(apiGet, `/api/video/${callId}/events${queryString}`);
            yield put(getVideoCallEventsSuccess(events, { callId, more, frames }));

            if (!more) {
                return true;
            }
        }
        catch (e) {
            const err = toError(e);
            yield put(getVideoCallEventsFailure(err, { callId }));
            return false;
        }
    }
}
